import { useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@rentspree/component-2023.components.atoms.button'
import Typography from '@rentspree/component-2023.components.atoms.typography'
import dynamic from 'next/dynamic'
import { forwardRef, useState, useRef, useEffect } from 'react'

import tracker from 'tracker'
import { LOCATION, MAIN_FEATURE } from 'tracker/events'

interface OverviewProps {
  video?: string
  description: string
}

const VideoTour = dynamic(() => import('../video-tour'), {
  ssr: false,
})

const Overview = forwardRef<HTMLDivElement, OverviewProps>(({ video, description }, ref) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const textRef = useRef<HTMLElement>(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    const el = textRef.current
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight)
    }
  }, [description])

  const handleExpandOverview = () => {
    tracker?.trackButtonClick({
      click_text: 'Show more about this property',
      location: LOCATION.PUBLIC_LISTING,
      screen_experience: isMobile ? 'mobile' : 'desktop',
      main_feature: MAIN_FEATURE.MARKET_LISTING,
    })
    setIsExpanded(!isExpanded)
  }

  return (
    <Box sx={{ paddingBottom: '40px', borderBottom: '1px solid #e0e0e0' }} id="overview" ref={ref}>
      <VideoTour video={video} />

      <Box>
        <Box sx={{ marginBottom: '16px' }}>
          <Typography variant="h3" fontSize={isMobile ? 24 : 32}>
            About this property
          </Typography>
        </Box>

        <Box
          ref={textRef}
          sx={{
            display: isExpanded ? 'block' : '-webkit-box',
            WebkitLineClamp: isExpanded ? 'none' : 5,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            marginBottom: '8px',
          }}
        >
          <Typography variant="body-medium">{description}</Typography>
        </Box>
        {isOverflowing && (
          <Button
            color="secondary"
            variant="text"
            size="small"
            onClick={handleExpandOverview}
            className="underline"
            wrapped
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </Button>
        )}
      </Box>
    </Box>
  )
})

Overview.displayName = 'Overview'

export default Overview
